import { Gauge, Clock, ListChecks, Wrench, FlaskConical } from "lucide-react";
import { CodeBlock } from "@/components/CodeBlock";

const DIFF_COLOR = {
  Beginner: "#00ff66",
  Intermediate: "#00e5ff",
  Advanced: "#ef4444",
};

export const LabMeta = ({ difficulty, time, prerequisites }) => {
  if (!difficulty && !time && !(prerequisites?.length)) return null;
  const color = DIFF_COLOR[difficulty] || "#00e5ff";
  return (
    <div data-testid="lab-meta" className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
      <div className="glass rounded-lg border border-slate-800 p-4">
        <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">
          <Gauge size={12} /> difficulty
        </div>
        <span className="font-mono font-bold text-sm" style={{ color }} data-testid="lab-difficulty">
          {difficulty || "—"}
        </span>
      </div>
      <div className="glass rounded-lg border border-slate-800 p-4">
        <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">
          <Clock size={12} /> hands-on time
        </div>
        <span className="font-mono font-bold text-sm text-slate-100" data-testid="lab-time">{time || "—"}</span>
      </div>
      <div className="glass rounded-lg border border-slate-800 p-4">
        <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">
          <ListChecks size={12} /> prerequisites
        </div>
        {prerequisites?.length ? (
          <ul className="space-y-1" data-testid="lab-prerequisites">
            {prerequisites.map((p, i) => (
              <li key={i} className="font-mono text-xs text-slate-300 flex gap-1.5">
                <span className="text-[#00ff66]">›</span> {p}
              </li>
            ))}
          </ul>
        ) : (
          <span className="font-mono text-xs text-slate-500">none</span>
        )}
      </div>
    </div>
  );
};

export const ToolsNeeded = ({ tools }) => {
  if (!tools?.length) return null;
  return (
    <section className="mb-10" data-testid="lab-tools">
      <div className="flex items-center gap-2 mb-4">
        <Wrench className="text-[#00e5ff]" size={18} />
        <h3 className="font-mono text-xl font-bold text-[#00e5ff]">Tools you'll need</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {tools.map((tool, i) => (
          <div key={i} className="rounded-lg border border-[#00e5ff]/25 bg-[#00e5ff]/[0.03] p-3">
            <div className="font-mono font-bold text-sm text-slate-100">{tool.name}</div>
            {tool.purpose ? <p className="text-xs text-slate-400 mt-1">{tool.purpose}</p> : null}
          </div>
        ))}
      </div>
    </section>
  );
};

export const Walkthrough = ({ steps, numbered = false }) => {
  if (!steps?.length) return null;
  const accent = "#00e5ff";
  return (
    <section className="mb-10" data-testid="lab-walkthrough">
      <div className="flex items-center gap-2 mb-5">
        <FlaskConical className="text-[#00e5ff]" size={20} />
        <h3 className="font-mono text-xl sm:text-2xl font-bold text-[#00e5ff]">Try it yourself — step by step</h3>
      </div>
      <div className="space-y-4">
        {steps.map((s, i) => (
          <div key={i} className={numbered ? "relative pl-9" : "pl-4 border-l-2"} style={numbered ? {} : { borderColor: accent }}>
            {numbered && (
              <span
                className="absolute left-0 top-0 w-6 h-6 rounded-full flex items-center justify-center font-mono text-xs font-bold"
                style={{ background: `${accent}15`, color: accent, border: `1px solid ${accent}55` }}
              >
                {i + 1}
              </span>
            )}
            <h4 className="font-mono font-semibold text-sm text-slate-100">{s.title}</h4>
            <p className="text-sm text-slate-400 mt-1 leading-relaxed">{s.description}</p>
            {s.code ? <CodeBlock code={s.code} accent={accent} label="run" /> : null}
          </div>
        ))}
      </div>
    </section>
  );
};
